import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, Phone, Sparkles, Star } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useProfile } from '@/api/profile'
import { useUpdateProfile } from '@/api/profile'
import { AUTO_CALLER_ID } from '@/lib/callerIdAuto'
import type { AvailableNumber } from '@/types/agents'

interface Props {
  numbers: AvailableNumber[]
  selectedCallerId: string | null
  onSelect: (phoneNumber: string) => void
  darkMode?: boolean
  compact?: boolean
  /** Puce dans une phrase (« Depuis [ligne] ») : la liste s'ouvre vers le bas. */
  inline?: boolean
  // Numéro que le mode Automatique utiliserait pour le numéro en cours de
  // composition (déjà résolu par le parent). null si indéterminé.
  autoResolvedNumber?: string | null
  // True si ce numéro vient du sticky (déjà utilisé pour ce contact) — affiche
  // un repère « mémorisé » pour que l'agent comprenne pourquoi ce numéro.
  autoResolvedFromSticky?: boolean
}

function formatPhoneShort(phone: string): string {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  return phone
}

export function CallerIdSelector({
  numbers,
  selectedCallerId,
  onSelect,
  darkMode = false,
  compact = false,
  inline = false,
  autoResolvedNumber = null,
  autoResolvedFromSticky = false,
}: Props) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isAuto = selectedCallerId === AUTO_CALLER_ID

  // Pour le bouton "étoile" qui persiste le numéro préféré directement depuis
  // ce sélecteur, sans avoir à passer par la page Profil.
  const { data: profile } = useProfile()
  const updateProfile = useUpdateProfile()
  const preferredCallerId = profile?.preferred_caller_id || ''

  const handleTogglePreferred = (
    e: React.MouseEvent<HTMLButtonElement>,
    phoneNumber: string,
  ) => {
    e.stopPropagation() // ne pas déclencher onSelect du parent
    const isAlreadyPreferred = preferredCallerId === phoneNumber
    const nextPreferred = isAlreadyPreferred ? '' : phoneNumber
    updateProfile.mutate(
      { preferred_caller_id: nextPreferred || undefined },
      {
        onSuccess: () => {
          toast.success(
            isAlreadyPreferred
              ? t('phone.callerId.preferredRemoved', 'Numéro préféré retiré')
              : t('phone.callerId.preferredSet', 'Numéro préféré défini'),
          )
        },
        onError: () => {
          toast.error(
            t('phone.callerId.preferredError', 'Impossible de sauvegarder'),
          )
        },
      },
    )
  }

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen])

  if (numbers.length <= 1) return null
  const selected = numbers.find((n) => n.phone_number === selectedCallerId) || numbers[0]
  const autoLabel = t('phone.callerId.auto', 'Automatique')

  return (
    <div ref={dropdownRef} className={cn('relative', inline ? 'inline-block' : compact ? 'mt-3' : 'mt-2')}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          inline
            ? 'inline-flex items-center gap-1.5 py-1 px-2 rounded-md transition-colors text-sm font-medium'
            : 'w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg transition-all text-sm',
          inline
            ? darkMode
              ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            : darkMode
              ? 'text-slate-400 hover:text-white hover:bg-slate-800/60 border border-slate-700/40'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 border border-gray-200/60',
        )}
      >
        {isAuto ? (
          <Sparkles className="w-3.5 h-3.5 flex-shrink-0 text-[#E85D3A]" />
        ) : (
          <Phone className="w-3.5 h-3.5 flex-shrink-0" />
        )}
        <span className="truncate">
          {isAuto ? (
            <>
              {autoLabel}
              {autoResolvedNumber && (
                <span className="opacity-70">
                  {' · '}
                  {formatPhoneShort(autoResolvedNumber)}
                </span>
              )}
              {autoResolvedNumber && autoResolvedFromSticky && (
                <span className="opacity-70">
                  {' · '}
                  {t('phone.callerId.autoSticky', 'mémorisé')}
                </span>
              )}
            </>
          ) : (
            // The name the customer gave the number, else the number itself.
            selected.label || formatPhoneShort(selected.phone_number)
          )}
        </span>
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 flex-shrink-0 transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className={cn(
              inline
                ? 'absolute top-full left-0 mt-1 w-72 z-50 rounded-lg overflow-hidden shadow-xl'
                : 'absolute bottom-full left-0 right-0 mb-1 z-50 rounded-lg overflow-hidden shadow-xl',
              darkMode ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-gray-200',
            )}
          >
            <div className="max-h-48 overflow-y-auto">
              {/* Mode Automatique : numéro local selon l'indicatif appelé.
                  N'utilise QUE les numéros de l'agent (pas les campagnes). */}
              <button
                type="button"
                onClick={() => {
                  onSelect(AUTO_CALLER_ID)
                  setIsOpen(false)
                }}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors border-b',
                  darkMode ? 'border-slate-800' : 'border-gray-100',
                  isAuto
                    ? darkMode
                      ? 'bg-[#E85D3A]/15 text-[#E85D3A]'
                      : 'bg-orange-50 text-[#E85D3A]'
                    : darkMode
                      ? 'text-slate-300 hover:bg-slate-800'
                      : 'text-gray-700 hover:bg-gray-50',
                )}
              >
                {isAuto ? (
                  <Check className="w-3.5 h-3.5 flex-shrink-0" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5 flex-shrink-0 text-[#E85D3A]" />
                )}
                <div className={cn('flex flex-col min-w-0', isAuto && '')}>
                  <span className="font-medium truncate">{autoLabel}</span>
                  <span
                    className={cn(
                      'text-xs',
                      darkMode ? 'text-slate-500' : 'text-gray-400',
                    )}
                  >
                    {t(
                      'phone.callerId.autoHint',
                      'Numéro local selon l\'indicatif appelé',
                    )}
                  </span>
                </div>
              </button>
              {numbers.map((num) => {
                const isSelected =
                  !isAuto && num.phone_number === selected.phone_number
                const isPreferred = num.phone_number === preferredCallerId
                return (
                  <div
                    key={num.id}
                    className={cn(
                      'w-full flex items-center gap-2 text-sm transition-colors',
                      darkMode
                        ? isSelected
                          ? 'bg-green-600/15 text-green-400'
                          : 'text-slate-300 hover:bg-slate-800'
                        : isSelected
                          ? 'bg-green-50 text-green-700'
                          : 'text-gray-700 hover:bg-gray-50',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        onSelect(num.phone_number)
                        setIsOpen(false)
                      }}
                      className="flex-1 flex items-center gap-2 px-3 py-2.5 text-left"
                    >
                      {isSelected && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                      <div className={cn('flex flex-col min-w-0', !isSelected && 'ml-5')}>
                        {/* The customer's name for the number, else the number alone. */}
                        <span className="font-medium truncate">
                          {num.label || formatPhoneShort(num.phone_number)}
                        </span>
                        {num.label && (
                          <span
                            className={cn(
                              'text-xs',
                              darkMode ? 'text-slate-500' : 'text-gray-400',
                            )}
                          >
                            {formatPhoneShort(num.phone_number)}
                          </span>
                        )}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleTogglePreferred(e, num.phone_number)}
                      disabled={updateProfile.isPending}
                      title={
                        isPreferred
                          ? t('phone.callerId.preferredTooltipOn', 'Numéro préféré (cliquer pour retirer)')
                          : t('phone.callerId.preferredTooltipOff', 'Définir comme numéro préféré')
                      }
                      aria-label={
                        isPreferred
                          ? t('phone.callerId.preferredTooltipOn', 'Numéro préféré (cliquer pour retirer)')
                          : t('phone.callerId.preferredTooltipOff', 'Définir comme numéro préféré')
                      }
                      className={cn(
                        'flex-shrink-0 p-2 rounded transition-colors',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        isPreferred
                          ? darkMode
                            ? 'text-amber-400 hover:bg-slate-800'
                            : 'text-amber-500 hover:bg-amber-50'
                          : darkMode
                            ? 'text-slate-600 hover:text-amber-400 hover:bg-slate-800'
                            : 'text-gray-300 hover:text-amber-500 hover:bg-amber-50',
                      )}
                    >
                      <Star
                        className="w-4 h-4"
                        fill={isPreferred ? 'currentColor' : 'none'}
                      />
                    </button>
                  </div>
                )
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
